import { NgModule } from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { TranslateModule } from '@ngx-translate/core';
import { HttpModule } from '@angular/http';
import { HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';
import { AutosizeModule } from 'ngx-autosize';
import {
    MatButtonModule, MatSelectModule, MatOptionModule, MatCheckboxModule, MatCardModule, MatDialogModule, MatFormFieldModule, MatSidenavModule,
    MatInputModule, MatAutocompleteModule, MatSlideToggleModule, MatIconModule, MatExpansionModule, MatTreeModule,
    MatProgressSpinnerModule, MatMenuModule, MatTooltipModule, MatListModule, MatDatepickerModule,
    MAT_DATE_FORMATS, DateAdapter
} from '@angular/material';
import { MatMomentDateModule, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { OwlDateTimeModule, OwlNativeDateTimeModule } from 'ng-pick-datetime';

const modules = [CommonModule, FormsModule, NgxDatatableModule, TranslateModule, HttpModule, HttpClientModule, BrowserAnimationsModule, NgbCollapseModule,
    AutosizeModule,
    MatButtonModule, MatSelectModule, MatOptionModule, MatCheckboxModule, MatCardModule, MatDialogModule, MatFormFieldModule, MatSidenavModule,
    MatInputModule, MatAutocompleteModule, MatSlideToggleModule, MatIconModule, MatExpansionModule, MatTreeModule,
    MatProgressSpinnerModule, MatMenuModule, MatTooltipModule, MatListModule, MatDatepickerModule, MatMomentDateModule,
    OwlDateTimeModule, OwlNativeDateTimeModule];

import { AlertComponent } from './components/alert/alert.component';

import { AlertService } from './services/alert.service';
import { BaseService } from './services/base.service';
import { AuthenticationService } from './services/authentication.service';
import { AccessGroupsService } from './services/access-groups.service';
import { LanguagesService } from './services/languages.service';
import { DatatableService } from './services/datatable.service';

import { ColumnFilterComponent } from './components/column-filter/column-filter.component';

import { EmailValidator } from './directives/email.validator.directive';
import { EqualToValidator } from './directives/equal-to';
import { FocusDirective } from './directives/focus.directive';
import { UniqueIdValidator } from './directives/unique-id-validate.directive';

import { OptionsPipe } from './pipes/options.pipe';
import { LocalizedDatePipe } from './pipes/localized-date.pipe';

import { DialogWindowComponent } from './components/dialog-window/dialog-window.component';
import { MenuOptionsComponent } from './components/menu-options/menu-options.component';
import { SubMenuComponent } from './components/sub-menu/sub-menu.component';
import { AccessTreeComponent } from './components/access-tree/access-tree.component';

import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { AuthenticateXHRBackend } from './interceptors/authenticate-xhr.backend';
import { XHRBackend } from '@angular/http';

import { HttpActivityInterceptor, HTTPStatus } from './interceptors/http-activity.interceptor';
import { HttpAuthorizationInterceptor } from './interceptors/http-authorization.interceptor';
import { AccessMenuGuard } from './guards/access-menu.guard';
import { CustomDateAdapter } from './miscellaneous/custom-date-adapter';
import { DateTimeAdapter, OWL_DATE_TIME_LOCALE, OWL_DATE_TIME_FORMATS, OwlDateTimeIntl } from 'ng-pick-datetime';
import { MomentDateTimeAdapter, OWL_MOMENT_DATE_TIME_ADAPTER_OPTIONS } from './miscellaneous/custom-date-time-adapter';
import { LocalizedIntl } from './miscellaneous/localized-intl';
import { TemplatedComponent } from './components/datatable-2-csv/templated.component';
import { DatatableCsvComponent } from './components/datatable-2-csv/datatable-csv.component';
import { MinValidatorDirective } from './directives/min.validator.directive';
import { MaxValidatorDirective } from './directives/max.validator.directive';

export { AEntity } from './models/a-entity';
export { EnmState } from './models/enums';
export { TransferHttpHelper } from './helpers/transfer-http-helper';

export { AlertComponent } from './components/alert/alert.component';
export { BaseComponent } from './components/base-component';
export { EditComponent } from './components/edit.component';
export { ListComponent } from './components/list.component';

export { Alert, AlertType } from './components/alert/alert';
export { ColumnFilterComponent, EnmFilterType, EnmPropertyType } from './components/column-filter/column-filter.component';
export { DialogWindowComponent } from './components/dialog-window/dialog-window.component';
export { DatatableCsvComponent } from './components/datatable-2-csv/datatable-csv.component';

export { AlertService } from './services/alert.service';
export { BaseHttpService } from './services/base-http.service';
export { BaseService } from './services/base.service';
export { CrudService } from './services/crud.service';
export { AuthenticationService } from './services/authentication.service';
export { AUsersService } from './services/a-users.service';
export { AccessGroupsService } from './services/access-groups.service';
export { DatatableService } from './services/datatable.service';

export { OptionsPipe } from './pipes/options.pipe';
export { LocalizedDatePipe } from './pipes/localized-date.pipe';

export { EmailValidator } from './directives/email.validator.directive';
export { EqualToValidator } from './directives/equal-to';
export { FocusDirective } from './directives/focus.directive';
export { UniqueIdValidator, UniqueIdConfig } from './directives/unique-id-validate.directive';

export { MultiTranslateHttpLoader } from './translation/multi-translate-http-loader';
export { HttpActivityInterceptor, HTTPStatus } from './interceptors/http-activity.interceptor';
export { HttpAuthorizationInterceptor } from './interceptors/http-authorization.interceptor';
export { AuthenticateXHRBackend } from './interceptors/authenticate-xhr.backend';

export { User } from './models/user';
export { Menu } from './models/rights/menu';
export { Right } from './models/rights/right';
export { AccessGroup } from './models/rights/access-group';
export { AccessRight } from './models/rights/access-right';
export { IMenuStructure } from './models/rights/i-menu-structure';
export { MenuOptionsComponent } from './components/menu-options/menu-options.component';
export { SubMenuComponent } from './components/sub-menu/sub-menu.component';
export { AccessTreeComponent } from './components/access-tree/access-tree.component';
export { Language } from './models/language';

export { AccessMenuGuard } from './guards/access-menu.guard';
export { LanguagesService } from './services/languages.service';
export { CustomDateAdapter } from './miscellaneous/custom-date-adapter';

export const MY_FORMATS = {
    parse: {
        dateInput: 'dateInput'
    },
    display: {
        dateInput: 'dateInput',
        monthYearLabel: 'monthYearLabel',
        dateA11yLabel: 'dateA11yLabel',
        monthYearA11yLabel: 'monthYearA11yLabel',
    },
    parseInput: 'parseInput',
    fullPickerInput: 'fullPickerInput',
    datePickerInput: 'datePickerInput',
    timePickerInput: 'timePickerInput',
    monthYearLabel: 'monthYearLabel',
    dateA11yLabel: 'dateA11yLabel',
    monthYearA11yLabel: 'monthYearA11yLabel',
};

@NgModule({
    imports: [...modules],
    declarations: [
        AlertComponent, ColumnFilterComponent, DialogWindowComponent, MenuOptionsComponent, SubMenuComponent, AccessTreeComponent,
        DatatableCsvComponent,
        //Directive
        FocusDirective,
        //Validator
        UniqueIdValidator, EqualToValidator, EmailValidator, MinValidatorDirective, MaxValidatorDirective,
        //Pipe
        OptionsPipe, LocalizedDatePipe,
        //Internal component
        TemplatedComponent
    ],
    exports: [
        AlertComponent, ColumnFilterComponent, DialogWindowComponent, MenuOptionsComponent, SubMenuComponent, AccessTreeComponent,
        DatatableCsvComponent,
        //Directive
        FocusDirective,
        //Validator
        UniqueIdValidator, EqualToValidator, EmailValidator, MinValidatorDirective, MaxValidatorDirective,
        //Pipe
        OptionsPipe, LocalizedDatePipe,
        //Modules
        ...modules
    ],
    providers: [
        OptionsPipe, LocalizedDatePipe,
        AlertService, BaseService, AuthenticationService, AccessGroupsService, LanguagesService, DatatableService,
        AccessMenuGuard,
        HttpActivityInterceptor, HTTPStatus, HttpAuthorizationInterceptor,
        { provide: XHRBackend, useClass: AuthenticateXHRBackend },
        { provide: HTTP_INTERCEPTORS, useClass: HttpActivityInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpAuthorizationInterceptor, multi: true },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
        { provide: OWL_DATE_TIME_FORMATS, useValue: MY_FORMATS },
        { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
        { provide: OWL_MOMENT_DATE_TIME_ADAPTER_OPTIONS, useValue: { useUtc: true } },
        { provide: OwlDateTimeIntl, useClass: LocalizedIntl },
        { provide: DateAdapter, useClass: CustomDateAdapter },
        { provide: DateTimeAdapter, useClass: MomentDateTimeAdapter },
    ],
    entryComponents: [
        DialogWindowComponent, TemplatedComponent
    ]
})
export class VoiteqAngularModule { }
